/**
 * `ChatList` — the `/inbox` page's WhatsApp / Instagram tab body (2026-08-06).
 *
 * A conversation list on the left, the selected conversation's thread on the
 * right, rendered by the SAME `ConversationView` the member and PR pages use —
 * with `readOnly` set, because answering belongs on the person's own card where
 * their credits, tags and AI switch are visible.
 *
 * The list is CONVERSATION-keyed, so a row often has no member and no PR
 * contact behind it. That is the point: on 2026-08-06 that was 13 of 14
 * Instagram conversations. When a person IS resolved, the row links out to
 * them; when not, the identifier is the identity and the thread still opens.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type ChatBoardConversation,
  type ChatThreadResponse,
} from "../lib/api";
import { studioFormat } from "../lib/studio-tz";
import { ConversationView } from "./ConversationView";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/**
 * Instagram's 24h customer-service window, as remaining time.
 *
 * Worth a chip rather than a timestamp because it is a HARD send boundary:
 * once it shuts, `send_instagram_dm` refuses with `instagram_window_closed`
 * before it ever reaches Meta. "Closes in 3h" is the actionable form.
 */
function windowChip(iso: string | null): { label: string; open: boolean } | null {
  if (iso === null) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  const msLeft = at - Date.now();
  if (msLeft <= 0) return { label: "window closed", open: false };
  const hours = Math.floor(msLeft / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  return {
    label: hours > 0 ? `closes in ${hours}h` : `closes in ${mins}m`,
    open: true,
  };
}

/**
 * Which conversation the pane should show, given what the user last clicked
 * and the list currently on screen.
 *
 * DERIVED, never synced through an effect — and that distinction is the whole
 * point of this function existing. The first version kept the selection in
 * state and re-pointed it from a `useEffect` on tab change. Switching tabs
 * updates `channel` immediately, but the effect's `setSelected` only lands on
 * the NEXT render, so the fetch effect fired once with the new channel and the
 * PREVIOUS channel's identifier: `GET /api/inbox-chats/whatsapp/<IGSID>` and
 * `/instagram/<phone>`, two 404s. It then self-corrected, which is exactly what
 * made it easy to miss — an error flash and two dead requests, then the right
 * thread. Caught on the live dashboard a minute after deploy.
 *
 * Deriving makes the mismatched pair unrepresentable: a `picked` value that is
 * not in the list on screen cannot win, so the channel and the identifier are
 * always from the same set.
 */
export function resolveSelectedConversation(
  picked: string | null,
  conversations: ReadonlyArray<{ readonly sender_identifier: string }>,
): string | null {
  if (
    picked !== null &&
    conversations.some((c) => c.sender_identifier === picked)
  ) {
    return picked;
  }
  return conversations[0]?.sender_identifier ?? null;
}

export function ChatList({
  channel,
  conversations,
}: {
  readonly channel: string;
  readonly conversations: ReadonlyArray<ChatBoardConversation>;
}): JSX.Element {
  const [picked, setPicked] = useState<string | null>(null);
  const [thread, setThread] = useState<ChatThreadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const selected = resolveSelectedConversation(picked, conversations);

  useEffect(() => {
    if (selected === null) {
      setThread(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .inboxChatThread(channel, selected)
      .then((r) => {
        if (cancelled) return;
        setThread(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message,
        );
        setThread(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channel, selected]);

  if (conversations.length === 0) {
    return (
      <p className="empty">
        No {channel === "whatsapp" ? "WhatsApp" : "Instagram"} conversations yet.
      </p>
    );
  }

  const person =
    thread === null
      ? null
      : thread.member_id !== null
        ? `/members/${encodeURIComponent(thread.member_id)}`
        : thread.outreach_target_id !== null
          ? `/pr/people/${encodeURIComponent(thread.outreach_target_id)}`
          : null;

  return (
    <div className="chat-split">
      <ul className="chat-list">
        {conversations.map((c) => {
          const win = windowChip(c.window_expires_at);
          return (
            <li key={`${c.channel}:${c.sender_identifier}`}>
              <button
                type="button"
                className={`chat-row${c.sender_identifier === selected ? " active" : ""}`}
                onClick={() => setPicked(c.sender_identifier)}
              >
                <span className="chat-row-head">
                  <span className="chat-row-name">{c.display_name}</span>
                  <span className="dc-muted">{fmtDateTime(c.last_at)}</span>
                </span>
                <span className="chat-row-meta dc-muted">
                  {c.message_count} message{c.message_count === 1 ? "" : "s"}
                  {c.member_id !== null ? " · member" : ""}
                  {c.outreach_target_id !== null ? " · PR" : ""}
                  {c.ai_override !== null ? ` · AI ${c.ai_override}` : ""}
                </span>
                <span className="chat-row-tags">
                  {c.awaiting_reply ? (
                    <span className="tag tag-error">awaiting reply</span>
                  ) : null}
                  {win !== null ? (
                    <span className={`tag${win.open ? "" : " tag-error"}`}>
                      {win.label}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="chat-pane">
        {error !== null ? (
          <p className="empty">Couldn&apos;t load the conversation ({error}).</p>
        ) : loading && thread === null ? (
          <p className="empty">Loading conversation…</p>
        ) : thread === null ? (
          <p className="empty">Pick a conversation.</p>
        ) : (
          <>
            <div className="pr-section-head">
              <h3 className="pr-heading">{thread.display_name}</h3>
              {person !== null ? (
                <button
                  type="button"
                  className="pr-toggle"
                  onClick={() => navigate(person)}
                >
                  Open full profile
                </button>
              ) : (
                <span className="subtle">
                  Not linked to a member or PR contact
                </span>
              )}
            </div>
            <ConversationView
              key={`${thread.channel}:${thread.sender_identifier}`}
              messages={thread.messages}
              phone={null}
              email={null}
              igsid={null}
              emailSubjectDefault=""
              onSent={() => {}}
              readOnly
            />
          </>
        )}
      </div>
    </div>
  );
}
